<script setup lang="ts">
// Shared hero banner for completion views.
//
// Colored band scoped to the whitelabel --ch-primary chain, with an
// illustration slot positioned in the top-right and heading text wrapping
// around it (matches certifid-vision's TaskHeroBanner).
//
// The sheet (BottomSheetModal) owns the close button, so this component
// doesn't render its own — keeps the chrome consistent across views.

withDefaults(defineProps<{
    // Optional inline status icon next to the heading.
    statusIcon?: 'success' | 'pending';
    // Where the illustration sits in the hero band. Portrait/square
    // illustrations read best anchored to the bottom; wide landscape
    // illustrations (e.g. the IDV ID-card render) read best centered.
    illustrationAlign?: 'bottom' | 'center';
}>(), {
    illustrationAlign: 'bottom',
});
</script>

<template>
    <div class="tch">
        <div class="tch__illustration" :class="`tch__illustration--${illustrationAlign}`">
            <slot name="illustration" />
        </div>

        <h2 class="tch__heading">
            <slot />
            <span v-if="statusIcon" class="tch__status" :class="`tch__status--${statusIcon}`">
                <svg v-if="statusIcon === 'success'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" fill="currentColor" />
                    <path d="M8 12l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5" />
                    <path d="M12 7v5l3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </span>
        </h2>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.tch {
    position: relative;
    background: $theme-primary-lightest;
    // Dedicated height rather than $portal-hero-min-h — that shared token
    // (158/140px) was clipping the taller portrait illustrations on desktop.
    // 200px gives every illustration ~55px of breathing room.
    min-height: 200px;
    padding: 44px $portal-section-px 20px;
    overflow: hidden;

    @media (min-width: 768px) {
        padding: 32px 32px 24px;
        border-radius: $portal-modal-radius;
    }

    &__illustration {
        position: absolute;
        right: 16px;
        width: 160px;
        pointer-events: none;
        color: var(--ch-primary, #156FBE);

        :deep(svg) {
            width: 100%;
            height: auto;
            display: block;
        }

        @media (min-width: 768px) {
            width: 176px;
            right: 24px;
        }

        &--bottom {
            bottom: 0;
        }

        &--center {
            top: 50%;
            transform: translateY(-46%);
        }
    }

    &__heading {
        font-size: $text-portal-h4;
        font-weight: 700;
        color: $portal-text;
        line-height: 1.3;
        margin: 0;
        max-width: 55%;
        // Slight padding so the heading doesn't crowd the drag handle on
        // mobile when the sheet uses overlayHandle.
        padding-top: 0;
    }

    &__status {
        display: inline-flex;
        vertical-align: middle;
        margin-left: 6px;
        width: 20px;
        height: 20px;

        svg {
            width: 100%;
            height: 100%;
        }
    }

    &__status--success {
        color: var(--ch-primary, #156FBE);
    }

    &__status--pending {
        color: $portal-text-mid;
        opacity: 0.6;
    }
}
</style>
